/**
 * The web app's records backend (Web app board cards 3.2–3.3): boards,
 * documents, calendar and projects read and write the SAME server records the
 * desktop app syncs, so what a user created on desktop is there on
 * app.apiboo.com and the other way round.
 *
 * Online-only by owner decision (no offline support on web): each scope is
 * bootstrapped from the server the first time a store reads it, held in memory
 * (scopeCache), and every write is applied locally at once, then pushed. A
 * pull follows every push and runs on a timer, so other devices' changes
 * arrive and the UI is told through `onRemoteChange`.
 *
 * Per scope, all network work runs on ONE promise chain, so pushes and pulls
 * never overlap. A pulled change for a record that still has an unpushed local
 * write is held back (the local edit would otherwise flicker back to the older
 * server copy) and applied afterwards only if the push did not supersede it.
 */
import { ScopeCache, opFieldsBytes } from './scopeCache';
import { recordRev } from './recordRev';
import { PUSH_MAX_OPS, httpTransport, scopeBasePath } from './syncTransport';
import { takePushBatch } from './pushBatch';
import { OUTBOX_ITEM_MAX_BYTES } from '../syncLimits';
import { isQuotaError } from '../../services/planQuota';

/** Same cap and error text as the desktop CAS write (records.rs). */
export const OP_MAX_BYTES = OUTBOX_ITEM_MAX_BYTES;
export const POLL_MS = 20000;

const statusOf = (err) => err?.response?.status;

export function createWebRecordsBackend({
  transport = httpTransport,
  onRemoteChange = () => {},
  // (scopeKey, [{ entityId, error }]) — ops the server refused.
  onRejected = () => {},
  // May this scope talk to the server? No = device-only, like personal:local
  // (e.g. a Free account that has not turned sync on).
  canSync = () => true,
  pollMs = POLL_MS,
  now = () => Date.now(),
  isHidden = () => typeof document !== 'undefined' && document.hidden,
} = {}) {
  const scopes = new Map();
  let timer = null;

  function startPolling() {
    if (timer || !pollMs) return;
    timer = setInterval(() => {
      if (isHidden()) return;
      for (const state of scopes.values()) {
        if (state.base && !state.disabled) enqueue(state, () => flushAndPull(state));
      }
    }, pollMs);
  }

  function enqueue(state, fn) {
    const run = state.chain.then(fn);
    state.chain = run.catch((err) => console.warn(`[web-records] ${state.key}:`, err?.message || err));
    return run;
  }

  function applyBootstrap(state, res, { prune }) {
    const changed = new Set();
    const serverIds = new Set();
    const entries = res?.extension_documents || [];
    for (const entry of entries) {
      serverIds.add(entry.entity_id);
      if (state.pending.get(entry.entity_id)) continue;
      const ext = entry.data?.extension_id;
      if (state.cache.applyServer(entry) && ext) changed.add(ext);
    }
    if (prune) {
      for (const row of state.cache.rows.values()) {
        // Never acknowledged (version null) = never on the server: a resync must
        // not erase it (e.g. a board the plan limit refused, kept on screen).
        if (row.deleted || row.version == null || serverIds.has(row.id) || state.pending.get(row.id)) continue;
        row.deleted = true;
        changed.add(row.extensionId);
      }
    }
    state.cache.maxSeq = Math.max(state.cache.maxSeq, Number(res?.max_seq) || 0);
    return changed;
  }

  function scope(scopeKey) {
    const key = String(scopeKey || '');
    let state = scopes.get(key);
    if (state) return state;
    state = {
      key,
      base: canSync(key) ? scopeBasePath(key) : null,
      cache: new ScopeCache(),
      pending: new Map(),
      held: new Map(),
      unsent: [],
      disabled: null,
      chain: Promise.resolve(),
    };
    scopes.set(key, state);
    state.ready = state.base
      ? transport
          .bootstrap(state.base)
          .then((res) => {
            applyBootstrap(state, res, { prune: false });
          })
          .catch((err) => {
            const status = statusOf(err);
            if (status === 402 || status === 403 || status === 404) {
              // Plan without cloud sync, or not a member: nothing to show, and
              // nothing this tab writes may be pushed. Kept, so it is not retried
              // on every read.
              state.disabled = status === 402 ? 'upgrade_required' : 'forbidden';
              console.warn(`[web-records] ${key}: sync unavailable (${state.disabled})`);
              return;
            }
            // The next read retries — unless reset() already replaced this state.
            if (scopes.get(key) === state) scopes.delete(key);
            throw err;
          })
      : Promise.resolve();
    if (state.base) startPolling();
    return state;
  }

  async function ready(scopeKey) {
    const state = scope(scopeKey);
    await state.ready;
    return state;
  }

  function markPending(state, ops) {
    for (const op of ops) state.pending.set(op.entity_id, (state.pending.get(op.entity_id) || 0) + 1);
  }

  function settle(state, ids, changed) {
    for (const id of ids) {
      const left = (state.pending.get(id) || 1) - 1;
      if (left > 0) {
        state.pending.set(id, left);
        continue;
      }
      state.pending.delete(id);
      const held = state.held.get(id);
      if (!held) continue;
      state.held.delete(id);
      const row = state.cache.rows.get(id);
      // The push produced a newer server version: the held copy is stale.
      if (row?.version != null && held.version != null && held.version < row.version) continue;
      const ext = held.data?.extension_id || row?.extensionId;
      if (state.cache.applyServer(held) && ext) changed.add(ext);
    }
  }

  function queueOps(state, ops) {
    if (!ops.length) return;
    if (!state.base || state.disabled) return; // device-only scope: memory is the store
    markPending(state, ops);
    state.unsent.push(...ops);
    enqueue(state, () => flushAndPull(state));
  }

  async function push(state, ops) {
    const sentAt = now();
    const wire = ops.map((op) => ({
      entity_type: 'extension_documents',
      entity_id: op.entity_id,
      op: op.op,
      fields: op.fields,
      base_version: state.cache.rows.get(op.entity_id)?.version ?? null,
      // When the edit was MADE: a retry after an outage must not beat a newer
      // edit from another device under last-write-wins.
      updated_at: op.at ?? sentAt,
      client_now_ms: sentAt,
    }));
    const res = await transport.push(state.base, wire);
    let rejected = false;
    const refused = [];
    for (const result of res?.results || []) {
      if (result.status === 'ok') {
        const row = state.cache.rows.get(result.entity_id);
        if (row && result.version != null) row.version = result.version;
      } else {
        refused.push({ entityId: result.entity_id, error: result.error });
        // A plan limit is not a conflict: re-reading the server would only
        // make the refused board/document vanish from the screen. It stays
        // visible for this session and the user is told it was not saved.
        if (!isQuotaError(result.error)) rejected = true;
        console.warn(`[web-records] ${state.key}: ${result.entity_id} rejected (${result.error})`);
      }
    }
    if (refused.length) onRejected(state.key, refused);
    return rejected;
  }

  async function pull(state, changed) {
    for (let guard = 0; guard < 1000; guard += 1) {
      const res = await transport.pull(state.base, state.cache.maxSeq);
      const changes = res?.changes || [];
      for (const change of changes) {
        if (state.pending.get(change.entity_id)) {
          state.held.set(change.entity_id, change);
          state.cache.maxSeq = Math.max(state.cache.maxSeq, change.server_seq || 0);
          continue;
        }
        const ext = change.data?.extension_id || state.cache.rows.get(change.entity_id)?.extensionId;
        if (state.cache.applyServer(change) && ext) changed.add(ext);
      }
      if (!res?.has_more || !changes.length) {
        state.cache.maxSeq = Math.max(state.cache.maxSeq, Number(res?.max_seq) || 0);
        return;
      }
    }
  }

  async function flushAndPull(state) {
    if (state.disabled) return;
    const changed = new Set();
    while (state.unsent.length) {
      const batch = takePushBatch(state.unsent, PUSH_MAX_OPS);
      let rejected;
      try {
        rejected = await push(state, batch);
      } catch (err) {
        const status = statusOf(err);
        if (status && status >= 400 && status < 500 && status !== 401 && status !== 408 && status !== 429) {
          // The server refused the batch itself (plan, membership, size): it
          // will refuse it again, so drop it and take the server's state.
          state.unsent.splice(0, batch.length);
          settle(state, batch.map((o) => o.entity_id), changed);
          if (status === 402 || status === 403) state.disabled = status === 402 ? 'upgrade_required' : 'forbidden';
          console.warn(`[web-records] ${state.key}: push refused (${status})`);
          if (!state.disabled) await resync(state, changed);
          break;
        }
        throw err; // network / 5xx: kept in `unsent`, retried on the next tick
      }
      state.unsent.splice(0, batch.length);
      settle(state, batch.map((o) => o.entity_id), changed);
      if (rejected) await resync(state, changed);
    }
    if (!state.disabled) await pull(state, changed);
    if (changed.size) onRemoteChange(state.key, changed);
  }

  async function resync(state, changed) {
    const res = await transport.bootstrap(state.base);
    for (const ext of applyBootstrap(state, res, { prune: true })) changed.add(ext);
  }

  return {
    async list({ workspaceId, extensionId, recordType = null, scopeId = null }) {
      const state = await ready(workspaceId);
      return state.cache.list(extensionId, recordType, scopeId);
    },

    async listMeta({ workspaceId, extensionId, recordType = null, scopeId = null }) {
      const state = await ready(workspaceId);
      const rows = state.cache.live(extensionId, recordType, scopeId);
      return Promise.all(
        rows.map(async (row) => ({
          id: row.id,
          record_type: row.recordType,
          parent_id: row.parentId ?? null,
          sort_key: row.sortKey,
          updated_at: row.updatedAt,
          rev: await recordRev(row.data),
        }))
      );
    },

    async upsert({ workspaceId, extensionId, record }) {
      const state = await ready(workspaceId);
      const op = state.cache.upsert(extensionId, record, now());
      if (op) queueOps(state, [op]);
      return null;
    },

    async casUpsert({ workspaceId, extensionId, record, expectedRev = null }) {
      const state = await ready(workspaceId);
      const bytes = opFieldsBytes(extensionId, record);
      // Rejected with a plain string, as the Tauri command rejects.
      // eslint-disable-next-line no-throw-literal
      if (bytes > OP_MAX_BYTES) throw `too_large:${bytes}:${OP_MAX_BYTES}`;
      if (expectedRev != null) {
        const row = state.cache.get(extensionId, record.id);
        // eslint-disable-next-line no-throw-literal
        if (!row || row.deleted) throw 'conflict:';
        const actual = await recordRev(row.data);
        // eslint-disable-next-line no-throw-literal
        if (actual !== expectedRev) throw `conflict:${actual}`;
      }
      const op = state.cache.upsert(extensionId, record, now());
      if (op) queueOps(state, [op]);
      return recordRev(record.data || {});
    },

    async delete({ workspaceId, extensionId, recordId }) {
      const state = await ready(workspaceId);
      const op = state.cache.remove(extensionId, recordId, now());
      if (op) queueOps(state, [op]);
      return null;
    },

    /** Upserts + deletes of one extension, queued as ONE push (recordsClient.writeRecords). */
    async batch({ workspaceId, extensionId, upserts = [], deletes = [] }) {
      const state = await ready(workspaceId);
      const ops = [];
      for (const record of upserts) {
        const op = state.cache.upsert(extensionId, record, now());
        if (op) ops.push(op);
      }
      for (const recordId of deletes) {
        const op = state.cache.remove(extensionId, String(recordId), now());
        if (op) ops.push(op);
      }
      queueOps(state, ops);
      return upserts.map(() => null);
    },

    async syncScope({ workspaceId, extensionId, scopeId, records = [] }) {
      const state = await ready(workspaceId);
      const ops = [];
      const seen = new Set();
      for (const record of records) {
        seen.add(record.id);
        const op = state.cache.upsert(extensionId, record, now());
        if (op) ops.push(op);
      }
      for (const row of state.cache.live(extensionId, null, scopeId)) {
        if (seen.has(row.id)) continue;
        const op = state.cache.remove(extensionId, row.id, now());
        if (op) ops.push(op);
      }
      queueOps(state, ops);
      return ops.length;
    },

    /** Wait until every queued push and pull has finished (tests, sign-out). */
    async idle() {
      await Promise.all([...scopes.values()].map((s) => s.chain));
    },

    /** Pull now, e.g. when the tab becomes visible again. */
    refresh() {
      for (const state of scopes.values()) {
        if (state.base && !state.disabled) enqueue(state, () => flushAndPull(state));
      }
    },

    /**
     * Forget every scope: the next read bootstraps again.
     *
     * `{ flush: true }` first sends what is still queued (a plan or sync-choice
     * change on the SAME account must not drop unsent edits). Without it the
     * queue is dropped — right for a session end or a different account.
     */
    async reset({ flush = false } = {}) {
      if (flush) {
        for (const state of scopes.values()) {
          if (state.base && !state.disabled && state.unsent.length) enqueue(state, () => flushAndPull(state));
        }
        await Promise.all([...scopes.values()].map((s) => s.chain));
      }
      scopes.clear();
      if (timer) clearInterval(timer);
      timer = null;
    },
  };
}
